import React, { useState, useEffect } from 'react';


interface SliderProps {
  options: number[];
  value?: number;
  onChange: ( value: number ) => void;
  ticks?: number[];
  unit?: string;
}


export const Slider: React.FC<SliderProps> = ( {
  options,
  value,
  onChange,
  ticks,
  unit = ''
} ) => {

  const valueToIndex = ( val: number ) => {
    const idx = options.findIndex( v => {return v === val;} );
    return idx === -1 ? 0 : idx;
  };

  const [ showedIndex, setShowedIndex ] = useState( valueToIndex( value != null ? value : options[ 0 ] ) );

  useEffect( () => {
    setShowedIndex( valueToIndex( value != null ? value : options[ 0 ] ) );
  }, [ value, options ] );


  const formatTick = ( tick: number ) => {
    return (
      <div className="slide-input-tick-container pb-4" key={ `slider_tick_${tick}` }>
        <div className='absolute border border-gray-300 h-2 bottom-5 mx-3'/>
        <span className="text-xs text-gray-600 text-center">
          { tick }
        </span>
      </div>
    );
  };


  const getTicks = () => {
    return options.map( opt => {return formatTick( opt );} );
  };


  const localChange = ( event: React.ChangeEvent<HTMLInputElement> ) => {
    setShowedIndex( parseInt( event.target.value ) );
  };


  return (
    <div className={ 'h-full pt-1 grid items-center w-full double_range_slider' }>
      <div className='mx-1.5 relative bottom-1.5'>
        <div className="absolute bg-blue-100 h-1 w-full rounded"/>
        <div className="relative value slide-input-tick-container" style={ { left: ( ( showedIndex / ( options.length - 1 ) ) * 100 ) + '%' } }>
          { `${ options[ showedIndex ] }${ unit }` }
        </div>
      </div>
      <input
        type="range"
        min={ 0 }
        max={ options.length - 1 }
        step={ 1 }
        value={ showedIndex }
        onChange={ localChange }
        onMouseUp={ () => {
          onChange( options[ showedIndex ] );
        } }
        onKeyUp={ () => {
          onChange( options[ showedIndex ] );
        } }
        className="slider-range duration-150"
      />
      <div className='relative bottom-0.5'>
        <div className="flex justify-between pt-1.5 w-full px-1.5">
          { ticks?.map( tick => {return formatTick( tick );} ) ?? getTicks() }
        </div>
      </div>
    </div>
  );
};


interface SliderRangeProps {
    min: number
    max: number
    valueStart: number
    valueEnd: number
    onChangeStart: ( ( value: number ) => void )
    onChangeEnd: ( ( value: number ) => void )
    step?: number | undefined
    visualStep?: number | undefined
    ticks?: number[]
    unit?: string
}

export const SliderRange: React.FC<SliderRangeProps> = ( {
  min,
  max,
  valueStart,
  valueEnd,
  onChangeStart,
  onChangeEnd,
  step = 1,
  visualStep = 1,
  ticks,
  unit = ''
} ) => {
  const [ showedStartValue, setShowedStartValue ] = useState( valueStart );
  const [ showedEndValue, setShowedEndValue ] = useState( valueEnd );

  useEffect( () => {
    setShowedStartValue( valueStart );
    setShowedEndValue( valueEnd );
  }, [ valueEnd, valueStart ] );

  const formatTick = ( tick: number ) => {
    return (
      <div className="slide-input-tick-container" key={ `slider_tick_${tick}` }>
        <div className='absolute border border-gray-300 h-2 bottom-0.5 mx-3'/>
        <span className="text-xs text-gray-600 text-center">
          {tick}
        </span>
      </div>
    );
  };

  const getTicks = () => {
    const tmpTicks: React.JSX.Element[] = [];

    if ( ( ( max - min ) / step ) > 10 ) {
      step = 1;
    }

    for ( let tick = min; tick <= max; tick += visualStep ) {
      tmpTicks.push( formatTick( tick ) );
    }
    return tmpTicks;
  };

  const localChangeStart = ( event: React.ChangeEvent<HTMLInputElement> ) => {
    const val = parseInt( event.target.value );
    if ( val >= showedEndValue ) {return;}
    setShowedStartValue( val );
  };

  const localChangeEnd = ( event: React.ChangeEvent<HTMLInputElement> ) => {
    const val = parseInt( event.target.value );
    if ( val <= showedStartValue ) {return;}
    setShowedEndValue( val );
  };

  return (
    <div className={'pt-1 w-full'}>
      <div className="double_range_slider ">
        <div className="flex justify-between pt-1.5 w-full px-1.5">
          { ticks?.map( tick => {return formatTick( tick );} ) ?? getTicks().map( tick => {return ( tick );} )  }
        </div>
        <span className="range_track" id="range_track"></span>

        <input
          type="range"
          id="start_hour_input"
          className="slider-range"
          min={min}
          max={max}
          value={showedStartValue}
          step={step}
          onChange={localChangeStart}
          onMouseUp={ () => {
            onChangeStart( showedStartValue );
          } }
          onKeyUp={ () => {
            onChangeStart( showedStartValue );
          } }
        />
        <input
          type="range"
          id="end_hour_input"
          className=""
          min={min}
          max={max}
          value={showedEndValue}
          step={step}
          onChange={localChangeEnd}
          onMouseUp={ () => {
            onChangeEnd( showedEndValue );
          } }
          onKeyUp={ () => {
            onChangeEnd( showedEndValue );
          } }
        />

        <div className='mx-1.5 relative bottom-[0.4rem]'>
          <div className="absolute bg-blue-100 h-1 w-full rounded"/>
          <div className="value slide-input-tick-container" style={{ left: ( ( ( showedStartValue - min ) / ( max - min ) * 100 ) ) + '%' }}>
            {`${showedStartValue}${unit}`}
          </div>
          <div className="value slide-input-tick-container" style={{ left: ( ( ( showedEndValue - min ) / ( max - min ) * 100 ) ) + '%' }}>
            {`${showedEndValue}${unit}`}
          </div>
          <div className="absolute bg-blue-300 h-1" style={{
            left: ( ( ( showedStartValue - min ) / ( max - min ) * 100 ) ) + '%',
            width: ( ( ( showedEndValue - showedStartValue ) / ( max - min ) * 100 ) ) + '%'
          }}/>
        </div>
      </div>
    </div>
  );
};